import React from "react";

import { isUnitLocked } from "../../utils/listingHelpers";
import {
  choiceRow,
  fieldError,
  fieldHint,
  fieldInput,
  fieldLabel,
  grid2,
} from "../listingEditorStyles";

/**
 * "Basisinformationen": what the listing is attached to.
 *
 * The controls come from the former ListingRelationFields plus the two
 * identity fields that used to sit in ListingDetailFields — availability date
 * and the server-owned slug. The address deliberately does NOT appear here;
 * it belongs to "Adresse & Lage" alone, so there is one place to edit it.
 *
 * Every rule is the one that was already in place: the co-living locks, the
 * "(bereits vergeben)" captions, the free-text room_id fallback for units
 * without rooms. The L0 net pins all of them.
 */
export default function ListingBasicInfoSection({
  form,
  setForm,
  listings,
  units,
  cities,
  listingType,
  editingListingId,
  unitsLoading,
  unitsError,
  citiesLoading,
  roomsLoading,
  roomsForSelectedUnit,
  isSelectedUnitColiving,
  formListingTypeAllowed,
  onChange,
  onListingTypeChange,
  onUnitChange,
  onCityChange,
}) {
  return (
    <>
      <fieldset className="m-0 border-0 p-0">
        <legend className={fieldLabel}>Listing-Typ *</legend>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className={choiceRow}>
            <input
              type="radio"
              name="listing_type"
              value="apartment"
              checked={listingType === "apartment"}
              onChange={onListingTypeChange}
              disabled={isSelectedUnitColiving && !formListingTypeAllowed.apartmentAllowed}
            />
            Gesamte Wohnung (Unit)
          </label>
          <label className={choiceRow}>
            <input
              type="radio"
              name="listing_type"
              value="room"
              checked={listingType === "room"}
              onChange={onListingTypeChange}
              disabled={isSelectedUnitColiving && !formListingTypeAllowed.roomAllowed}
            />
            Einzelnes Zimmer (Co-Living)
          </label>
        </div>
      </fieldset>

      <div className={grid2}>
        <div>
          <label htmlFor="listing-unit-id" className={fieldLabel}>
            Unit (Apartment) *
          </label>
          {unitsLoading && <p className="mb-2 text-[13px] text-muted-foreground">Units werden geladen…</p>}
          {unitsError && <p className={fieldError}>{unitsError}</p>}
          <select
            id="listing-unit-id"
            name="unit_id"
            value={form.unit_id}
            onChange={onUnitChange}
            required
            disabled={unitsLoading}
            className={fieldInput}
          >
            <option value="">— Unit wählen —</option>
            {units.map((u) => {
              const id = u.id;
              const label =
                [u.short_unit_id, u.address, u.zip, u.city]
                  .filter(Boolean)
                  .join(" · ") || id;
              const locked = isUnitLocked(u, listings, editingListingId);
              return (
                <option key={id} value={id} disabled={locked}>
                  {locked ? `${label}  (bereits vergeben)` : label}
                </option>
              );
            })}
          </select>
          {!unitsLoading && !unitsError && units.length === 0 && (
            <p className={fieldHint}>
              Keine Units in der Datenbank. Zuerst Units anlegen (z.B. per Seed-Script).
            </p>
          )}
        </div>

        {listingType === "room" && (
          <div>
            <label htmlFor="listing-room-id" className={fieldLabel}>
              Room *
            </label>
            {roomsLoading && <p className={fieldHint}>Rooms werden geladen…</p>}
            {!roomsLoading && roomsForSelectedUnit.length > 0 ? (
              <select
                id="listing-room-id"
                name="room_id"
                value={form.room_id}
                onChange={onChange}
                required
                className={fieldInput}
              >
                <option value="">— Room wählen —</option>
                {roomsForSelectedUnit.map((r) => {
                  const roomLocked = listings.some(
                    (l) =>
                      l.unit_id === form.unit_id &&
                      l.room_id === r.id &&
                      l.id !== editingListingId
                  );
                  return (
                    <option key={r.id} value={r.id} disabled={roomLocked}>
                      {roomLocked
                        ? `${r.name || r.id}  (bereits vergeben)`
                        : r.name || r.id}
                    </option>
                  );
                })}
              </select>
            ) : !roomsLoading ? (
              <>
                <input
                  id="listing-room-id"
                  type="text"
                  name="room_id"
                  value={form.room_id}
                  onChange={onChange}
                  required
                  placeholder="room_id (UUID aus Datenbank)"
                  className={fieldInput}
                />
                <p className={fieldHint}>
                  Keine Rooms für diese Unit in der Datenbank. room_id manuell eingeben oder Rooms für die Unit anlegen.
                </p>
              </>
            ) : null}
          </div>
        )}
      </div>

      <div className={grid2}>
        <div>
          <label htmlFor="listing-city-id" className={fieldLabel}>
            Stadt (City) *
          </label>
          {citiesLoading ? (
            <p className={fieldHint}>Städte werden geladen…</p>
          ) : (
            <select
              id="listing-city-id"
              name="city_id"
              value={form.city_id}
              onChange={onCityChange}
              required
              className={fieldInput}
            >
              <option value="">— Stadt wählen —</option>
              {cities.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name_de} ({c.slug})
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <label htmlFor="listing-available-from" className={fieldLabel}>
            Verfügbar ab
          </label>
          <input
            id="listing-available-from"
            type="date"
            className={fieldInput}
            value={form.available_from}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, available_from: e.target.value }))
            }
          />
          <p className={fieldHint}>Optional. Leer lassen für "sofort verfügbar".</p>
        </div>
      </div>

      <div>
        <label htmlFor="listing-slug" className={fieldLabel}>
          Slug
        </label>
        <input
          id="listing-slug"
          type="text"
          name="slug"
          value={form.slug}
          readOnly
          placeholder="(automatisch generiert)"
          className={`${fieldInput} bg-muted`}
        />
        <p className={fieldHint}>Der Slug wird automatisch aus dem Titel generiert.</p>
      </div>
    </>
  );
}
